import { match, P } from 'ts-pattern'
import { Link } from 'react-router-dom'
import { Button } from 'ui/Button/Button'
import { Skeleton } from 'shadcdn/ui/skeleton'
import type { VersionState } from 'features/versions/types'
import type { SoftwareSchema } from 'openapi'
import { isRunningInElectron } from 'utils/electron'
import { useCategory } from 'features/categories/hooks'
import { useSoftwareQuery, useVersionQuery } from 'features/software/hooks'
import { SoftwareRoutePath } from 'router'
import { cn } from 'utils/cn'
import { appAPI } from 'utils/app-api'

type Props = {
  versionId: number
  versionState: VersionState
}

/**
 * Displays a single installation row in the Downloads page
 * Shows software name, installation progress, and cancel button
 */
export function InstallationRow(props: Props) {
  const { versionId, versionState } = props
  const { version, isLoading: versionLoading } = useVersionQuery(versionId)
  const { data: software, isLoading: softwareLoading } = useSoftwareQuery(version?.software_id ?? 0)
  const [category] = useCategory(software?.category_id ?? undefined)

  if (versionLoading || softwareLoading) {
    return <InstallationRowSkeleton versionState={versionState} />
  }

  const progress: number | null = match(versionState)
    .with({ status: P.union('downloading', 'unpacking', 'installing') }, (state) => Math.round(state.progress))
    .otherwise(() => null)

  const statusText = match(versionState)
    .with({ status: 'downloading' }, () => 'Downloading')
    .with({ status: 'unpacking' }, () => 'Unpacking')
    .with({ status: 'installing' }, () => 'Installing')
    .with({ status: 'canceling' }, () => 'Canceling')
    .with({ status: 'error' }, (state) => `Error: ${state.message}`)
    .with({ status: 'done' }, () => 'Complete')
    .with({ status: 'cancelled' }, () => 'Cancelled')
    .otherwise(() => 'Unknown')

  const canCancel = ['downloading', 'unpacking', 'installing'].includes(versionState.status)

  const handleCancel = () => {
    if (!isRunningInElectron || !canCancel) return
    appAPI.cancelInstall(versionId.toString())
  }

  const isCompleted = versionState.status === 'done'

  return (
    <div
      className={cn('flex items-center justify-between p-4 border rounded-lg', {
        'bg-green-50 border-green-200': isCompleted,
        'bg-white border-gray-200': !isCompleted,
      })}
    >
      <div className="flex-1 mr-4">
        <div className="flex items-center justify-between mb-2">
          <SoftwareTitle software={software} category={category} version={version} versionId={versionId} />
          <span
            className={cn('text-sm', {
              'text-green-600': isCompleted,
              'text-gray-500': !isCompleted,
            })}
          >
            {statusText}
          </span>
        </div>

        {progress !== null && (
          <div className="w-full">
            <div className="w-full bg-gray-100 rounded-full h-2 mb-1">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="text-xs text-gray-400 text-right">{progress}%</div>
          </div>
        )}
      </div>

      {canCancel && (
        <Button color="danger" size="sm" outline onClick={handleCancel} className="ml-4">
          Cancel
        </Button>
      )}
    </div>
  )
}

function SoftwareTitle(props: { software?: SoftwareSchema; category?: any; version?: any; versionId: number }) {
  const { software, category, version, versionId } = props

  // Format: "SoftwareName by VendorName vVersion"
  let displayName = `Version ${versionId}` // fallback

  if (software && version) {
    displayName = software.name
    if (software.vendor) {
      displayName += ` by ${software.vendor}`
    }
    if (version.version) {
      displayName += ` v${version.version}`
    }
  }

  if (!software || !category) {
    return <h3 className="font-medium text-gray-900">{displayName}</h3>
  }

  const softwareLink = SoftwareRoutePath({
    softwareId: software.id,
    categorySlug: category.slug,
    vendorSlug: software.vendorSlug,
    softwareSlug: software.slug,
  })

  return (
    <Link to={softwareLink} className="font-medium text-gray-900 hover:text-blue-600 hover:underline">
      {displayName}
    </Link>
  )
}

function InstallationRowSkeleton(props: { versionState: VersionState }) {
  const { versionState } = props

  const progress: number | null = match(versionState)
    .with({ status: P.union('downloading', 'unpacking', 'installing') }, (state) => Math.round(state.progress))
    .otherwise(() => null)

  const statusText = match(versionState)
    .with({ status: 'downloading' }, () => 'Downloading')
    .with({ status: 'unpacking' }, () => 'Unpacking')
    .with({ status: 'installing' }, () => 'Installing')
    .with({ status: 'canceling' }, () => 'Canceling')
    .with({ status: 'error' }, (state) => `Error: ${state.message}`)
    .with({ status: 'done' }, () => 'Complete')
    .with({ status: 'cancelled' }, () => 'Cancelled')
    .otherwise(() => 'Unknown')

  const canCancel = ['downloading', 'unpacking', 'installing'].includes(versionState.status)
  const isCompleted = versionState.status === 'done'

  return (
    <div
      className={cn('flex items-center justify-between p-4 border rounded-lg', {
        'bg-green-50 border-green-200': isCompleted,
        'bg-white border-gray-200': !isCompleted,
      })}
    >
      <div className="flex-1 mr-4">
        <div className="flex items-center justify-between mb-2">
          <Skeleton className="h-5 w-48" />
          <span
            className={cn('text-sm', {
              'text-green-600': isCompleted,
              'text-gray-500': !isCompleted,
            })}
          >
            {statusText}
          </span>
        </div>

        {progress !== null && (
          <div className="w-full">
            <div className="w-full bg-gray-100 rounded-full h-2 mb-1">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="text-xs text-gray-400 text-right">{progress}%</div>
          </div>
        )}
      </div>

      {canCancel && (
        <Button color="danger" size="sm" outline disabled className="ml-4">
          Cancel
        </Button>
      )}
    </div>
  )
}
